import type { ApiResult } from "../api";

/**
 * The two JSON helpers every data module is built from, parameterised by the
 * function that actually performs the request. The staff client passes its
 * `request()` (single-flight refresh, Hub redirect on 401); the portal passes a
 * plain fetch that must never redirect a customer to the Hub. The shape of
 * "data or a thrown Response" is the same for both, so it lives once.
 */
export type Requester = <T>(path: string, init?: RequestInit) => Promise<ApiResult<T>>;

/** GET, returning data or throwing the status as a Response — a loader that
 *  throws a Response hands control to the route's errorElement. */
export async function getJson<T>(request: Requester, path: string): Promise<T> {
  const res = await request<T>(path);
  if (res.status === 200 && res.data) return res.data;
  throw new Response(res.status === 404 ? "Not found" : "Request failed", { status: res.status || 500 });
}

/** POST/PATCH/DELETE with a JSON body, returning the parsed data or throwing
 *  the status as a Response whose text is the server's own message when it
 *  sent one ("already used by SpaceX") rather than the generic `fail`. */
export async function sendJson<T>(request: Requester, path: string, method: "POST" | "PATCH" | "DELETE", body: unknown, fail: string): Promise<T> {
  const init: RequestInit = { method };
  if (body !== undefined) {
    init.headers = { "Content-Type": "application/json" };
    init.body = JSON.stringify(body);
  }
  const res = await request<T>(path, init);
  if ((res.status === 200 || res.status === 201) && res.data) return res.data;
  const detail = (res.data as { error?: unknown } | null)?.error;
  throw new Response(typeof detail === "string" ? detail : fail, { status: res.status || 500 });
}
